import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import TodayBar from "@/components/TodayBar";
import Performance from "@/pages/Performance";
import AthleteTaskLibrary from "@/pages/AthleteTaskLibrary";
import { CalendarWorkspace } from '@/components/coaching/CalendarWorkspace';
import { BudgetWorkspace } from "@/components/academy/BudgetWorkspace";
import Budget from "@/pages/Budget";
import ChangeLog from "@/components/ChangeLog";
import { StrategyWorkbench } from '@/components/coaching/StrategyWorkbench';
import AthleteChat from "@/components/AthleteChat";
import AvailabilityControl from "@/components/AvailabilityControl";
import AthletePhoto from "@/components/AthletePhoto";
import { Users, ChevronDown, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/perms";
import { toast } from "sonner";
import AthleteLinkCard from "@/components/AthleteLinkCard";
import { TemplateLibrary } from "@/components/plan/TemplateLibrary";
import { Link } from "react-router-dom";

const TABS = [
  ["plan", "Plan", "plan"], ["templates", "Templates", "plan"], ["training", "Training", "performance"],
  ["library", "Insights", "performance"], ["strategy", "Strategy", "matches"],
  ["chat", "Chat", "plan"], ["budget", "Budget", "budget"], ["log", "Logs", "performance"],
];

export default function AthleteWorkspace() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { id: pathId } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const id = pathId || sp.get("id");
  const [athletes, setAthletes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const requestedTab = sp.get("tab") || (can('plan') ? 'plan' : can('performance') || can('physio') || can('nutrition') ? 'training' : can('matches') ? 'strategy' : 'chat');
  const tab = ['today','studio','calendar'].includes(requestedTab) ? 'plan' : requestedTab === 'performance' ? 'training' : requestedTab === 'matches' ? 'strategy' : requestedTab;

  const loadAthletes = () => api.get("/athletes").then(r => setAthletes(r.data)).catch(() => setError("Could not load your workspace. Please try again.")).finally(() => setLoaded(true));
  useEffect(() => { loadAthletes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // if no id, redirect to first athlete
    if (!id && athletes[0]) navigate(`/athlete/${athletes[0].athlete_id}${window.location.search}`, { replace: true });
  }, [id, athletes, navigate]);

  const setTab = (t) => setSp((p) => { p.set("tab", t); return p; });
  const current = athletes.find((a) => a.athlete_id === id);
  if (error) return <p className="p-8 text-traffic-red" role="alert" data-testid="workspace-error">{error}</p>;
  if (!loaded) return <p className="p-8 text-sm text-muted-foreground" data-testid="workspace-loading">Loading your workspace…</p>;
  if (!athletes.length) return <div className="p-8 sm:p-16" data-testid="workspace-empty">{user?.role === "athlete" ? <><AthleteLinkCard onLinked={() => loadAthletes()} /><Link to="/join" data-testid="workspace-join-academy" className="block text-center text-sm underline mt-8">Have an academy invitation? Join here</Link></> : <><h1 className="font-heading text-4xl font-black">Your athletes start here.</h1><p className="text-sm text-muted-foreground my-5">Add an athlete you coach, or ask your academy admin to assign athletes to you.</p><Link to="/add-athlete" data-testid="workspace-add-athlete" className="inline-flex rounded-full bg-foreground text-background px-5 py-3">Add athlete</Link></>}</div>;
  if (!id) return null;
  if (!current) return <p className="p-8 text-sm" data-testid="workspace-forbidden">This athlete isn't available in your workspace.</p>;
  const visibleTabs = TABS.filter(([t, , sec]) => (t === "training" ? can("performance") || can("physio") || can("nutrition") : can(sec)) && !(user?.role === "athlete" && ["templates", "log"].includes(t)));
  const allowed = visibleTabs.some(([t]) => t === tab);

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center px-4 sm:px-8 gap-3 py-3 sticky top-0 bg-background z-30">
        {current && <AthletePhoto athlete={current} size="w-9 h-9" canEdit={user?.role !== "athlete" || current.linked_user_id === user?.user_id}
          onChange={() => api.get("/athletes").then((r) => setAthletes(r.data))} />}
        {user?.role === "athlete" ? <h1 data-testid="workspace-athlete-name" className="font-heading text-lg font-bold">{current.name} · My workspace</h1> : <div className="relative">
          <select data-testid="workspace-athlete-select" value={id} onChange={(e) => navigate(`/athlete/${e.target.value}?${new URLSearchParams({...Object.fromEntries(sp),tab})}`)} className="appearance-none border border-border bg-background text-sm font-medium pl-3 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground">
            {athletes.map((a) => <option key={a.athlete_id} value={a.athlete_id}>{a.name}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-2 top-2.5 pointer-events-none text-muted-foreground" />
        </div>}
        {current && user?.role !== "athlete" && <AvailabilityControl athlete={current} onChange={() => api.get("/athletes").then((r) => setAthletes(r.data))} />}
        {current && user?.role !== "athlete" && user?.role !== "demo" && (
          <button
            data-testid="delete-athlete-btn"
            onClick={async () => {
              const label = `${current.name} (${current.athlete_id})`;
              const confirmed = window.confirm(
                `Remove ${label}?\n\nEverything attached — tasks, wearable logs, plans, reports, change log — will be permanently deleted.\n\nThis cannot be undone.`
              );
              if (!confirmed) return;
              try {
                const r = await api.delete(`/athletes/${current.athlete_id}`);
                const rm = r.data?.removed || {};
                const summary = Object.entries(rm).map(([k, v]) => `${k}: ${v}`).join(" · ") || "no linked data";
                toast.success(`Deleted ${current.name}`, { description: summary });
                const rest = await api.get("/athletes").then((x) => x.data);
                setAthletes(rest);
                if (rest[0]) navigate(`/athlete/${rest[0].athlete_id}?tab=${tab}`, { replace: true });
                else navigate("/", { replace: true });
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not delete athlete");
              }
            }}
            className="flex items-center gap-1.5 border border-traffic-red/40 text-traffic-red px-3 py-2 text-sm hover:bg-traffic-red/10 transition-colors"
            title="Remove this athlete and all their data"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        )}
        {user?.role !== "athlete" && <button data-testid="squad-view-btn" onClick={() => navigate("/command-center")} className="ml-auto flex items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-surface transition-colors">
          <Users className="w-4 h-4" /> Command Centre
        </button>}
      </header>

      <TodayBar athleteId={id} />

      <label className="sm:hidden block px-4 py-3 border-b border-border text-xs">Workspace view
        <select data-testid="workspace-mobile-view" className="inp mt-1" value={tab} onChange={e=>setTab(e.target.value)}>{visibleTabs.map(([key,label])=><option key={key} value={key}>{label}</option>)}</select>
      </label>
      <div className="hidden sm:flex border-b border-border overflow-x-auto sticky top-16 bg-background z-20">
        {visibleTabs.map(([t, label]) => (
          <button key={t} data-testid={`ws-tab-${t}`} onClick={() => setTab(t)} className={`px-5 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>
        ))}
      </div>

      {allowed ? <div data-testid={`ws-panel-${tab}`}>
        {tab === 'plan' && <CalendarWorkspace key={id} athleteId={id}/>}
        {tab === "templates" && <TemplateLibrary key={id} mode="athlete" athleteId={id} academyId={current.academy_id || ""} sport={current.sport || ""} />}
        {tab === "training" && <Performance athleteId={id} embedded />}
        {tab === "library" && <div className="p-8"><AthleteTaskLibrary athleteId={id} embedded /></div>}
        {tab === "budget" && (current?.academy_id && user?.role !== "athlete" ? <div className="p-5 sm:p-8"><BudgetWorkspace key={`${current.academy_id}:${id}`} academyId={current.academy_id} athleteId={id}/></div> : <Budget athleteId={id}/>)}
        {tab === "strategy" && <StrategyWorkbench key={id} athleteId={id}/>}
        {tab === "chat" && <AthleteChat key={id} athleteId={id} />}
        {tab === "log" && <ChangeLog athleteId={id} />}
      </div> : <p className="p-8 text-sm" data-testid="workspace-tab-unavailable">This section isn't available for your role.</p>}
    </div>
  );
}
